<template>
  <div class="invoices-container">
    <!-- Header Section -->
    <div class="header-card">
      <v-row>
        <v-col :cols="isMobile ? 12 : 4">
          <h4 class="page-title" :style="{ color: getOutlineColor }">
            <v-icon large :color="getOutlineColor" class="mr-2"
              >mdi-file-document-multiple</v-icon
            >
            Invoices
          </h4></v-col
        >
        <v-col :cols="isMobile ? 12 : 4">
          <!-- Search Bar -->
          <v-text-field
            v-model="searchInvoices"
            outlined
            dense
            placeholder="Search invoices..."
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="search-field"
            clearable
          ></v-text-field>
        </v-col>

        <!-- Action Buttons -->
        <v-col :cols="isMobile ? 12 : 4">
          <div class="action-buttons">
            <v-btn
              :color="getThemeColor"
              dark
              rounded
              elevation="2"
              @click="openMarkPaidDialog()"
              small
              class="action-btn"
            >
              <v-icon left small>mdi-check-circle</v-icon>
              Mark Paid
            </v-btn>

            <v-btn
              color="error"
              dark
              rounded
              elevation="2"
              @click="deleteSelectedInvoices()"
             small
              class="action-btn"
            >
              <v-icon left small>mdi-delete</v-icon>
              Delete
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Mobile View -->
    <div class="mobile-view d-md-none">
      <v-card
        v-for="invoice in filteredInvoices"
        :key="invoice.id"
        class="invoice-card mb-4"
        elevation="2"
      >
        <v-card-title class="pb-2">
          <v-checkbox
            :value="invoice"
            v-model="selectedInvoicesForPayment"
            class="mt-0 pt-0"
            hide-details
          ></v-checkbox>
          <span class="invoice-number">{{ invoice.invoice_number }}</span>
          <v-spacer></v-spacer>
          <v-chip
            small
            :color="determineColor(invoice)"
            dark
            class="status-chip"
          >
            {{ getInvoiceStatus(invoice) }}
          </v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <div class="invoice-detail">
            <v-icon small class="mr-2">mdi-account</v-icon>
            <span class="detail-label">Client:</span>
            <span class="detail-value" v-if="invoice.client">
              {{ invoice.client.name }}
              <span v-if="invoice.case" class="case-title">
                [{{ invoice.case.title }}]
              </span>
            </span>
          </div>

          <div class="invoice-detail">
            <v-icon small class="mr-2">mdi-calendar</v-icon>
            <span class="detail-label">Date:</span>
            <span class="detail-value">{{ invoice.invoice_date }}</span>
          </div>

          <div class="invoice-detail">
            <v-icon small class="mr-2">mdi-cash-multiple</v-icon>
            <span class="detail-label">Total:</span>
            <span class="detail-value">
              {{ getCurrency }}{{ formatNumber(invoice.calculation.total) }}
            </span>
          </div>

          <div class="invoice-detail">
            <v-icon small class="mr-2">mdi-cash</v-icon>
            <span class="detail-label">Paid:</span>
            <span class="detail-value">
              {{ getCurrency }}{{ formatNumber(invoice.totalPayment) }}
            </span>
          </div>

          <div
            class="invoice-detail"
            v-if="invoice.totalPayment < invoice.calculation.total"
          >
            <v-icon small class="mr-2">mdi-alert-circle</v-icon>
            <span class="detail-label">Due:</span>
            <span class="detail-value text-error">
              {{ getCurrency
              }}{{
                formatNumber(invoice.calculation.total - invoice.totalPayment)
              }}
            </span>
          </div>

          <div
            class="invoice-detail"
            v-if="invoice.totalPayment < invoice.calculation.total"
          >
            <v-icon small class="mr-2">mdi-calendar-clock</v-icon>
            <span class="detail-label">Due Date:</span>
            <span class="detail-value">{{ invoice.due_date }}</span>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn
            small
            :color="getThemeColor"
            dark
            @click="editSelectedInvoice(invoice)"
          >
            <v-icon small left>mdi-pencil</v-icon>
            Edit
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="success"
            dark
            @click="printPaymentReceipt(invoice)"
            :disabled="invoice.totalPayment === 0"
          >
            <v-icon small left>mdi-printer</v-icon>
            Receipt
          </v-btn>
        </v-card-actions>
      </v-card>

      <div v-if="filteredInvoices.length === 0" class="empty-state">
        <v-icon size="64" color="grey lighten-1"
          >mdi-file-document-outline</v-icon
        >
        <p class="mt-4">No invoices found</p>
      </div>
    </div>

    <!-- Desktop View -->
    <v-card class="desktop-view d-none d-md-block" elevation="2">
      <v-data-table
        :headers="invoiceHeaders"
        :items="invoices"
        :items-per-page="10"
        :search="searchInvoices"
        class="modern-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <template v-slot:item.selection="{ item }">
          <div class="action-cell">
            <v-checkbox
              :value="item"
              v-model="selectedInvoicesForPayment"
              hide-details
              class="ma-0"
            ></v-checkbox>
            <v-btn
              icon
              small
              :color="getOutlineColor"
              @click="editSelectedInvoice(item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="success"
              @click="printPaymentReceipt(item)"
              :disabled="item.totalPayment === 0"
            >
              <v-icon small>mdi-printer</v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:item.invoice_number="{ item }">
          <span class="font-weight-bold">{{ item.invoice_number }}</span>
        </template>

        <!-- <template v-if="item.client" v-slot:item.client.name="{ item }">
          <div class="client-info">
            <div class="client-name" v-if="item.client">{{ item.client.name }}</div>
            <div v-if="item.case" class="client-case">
              {{ item.case.title }}
            </div>
          </div>
        </template> -->

        <!-- <template v-slot:item.calculation.total="{ item }">
          <span class="amount-text">
            {{ getCurrency }}{{ formatNumber(item.calculation.total) }}
          </span>
        </template> -->

        <template v-slot:item.paid="{ item }">
          <v-chip
            small
            :color="item.totalPayment === 0 ? 'grey' : 'success'"
            dark
          >
            <v-icon left x-small>
              {{ item.totalPayment === 0 ? "mdi-close" : "mdi-check" }}
            </v-icon>
            {{
              item.totalPayment === 0
                ? "Unpaid"
                : getCurrency + formatNumber(item.totalPayment)
            }}
          </v-chip>
        </template>

        <template v-slot:item.due_date="{ item }">
          <v-chip
            v-if="item.totalPayment >= item.calculation.total"
            small
            color="success"
            dark
          >
            <v-icon left x-small>mdi-check-circle</v-icon>
            Completed
          </v-chip>
          <v-chip
            v-else
            small
            :color="isOverdue(item.due_date) ? 'error' : 'warning'"
            dark
          >
            <v-icon left x-small>mdi-calendar-clock</v-icon>
            {{ item.due_date }}
          </v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="determineColor(item)" dark>
            {{ getStatusText(item) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Mark Paid Dialog -->
    <v-dialog v-model="markPaidDialog" max-width="900" persistent scrollable>
      <v-card class="mark-paid-card">
        <v-card-title class="dialog-title">
          <v-icon left :color="getThemeColor">mdi-check-circle</v-icon>
          Mark Invoices as Paid
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-alert type="info" text dense class="mb-4">
            {{ selectedInvoicesForPayment.length }}
            {{
              selectedInvoicesForPayment.length === 1 ? "invoice" : "invoices"
            }}
            selected for payment
          </v-alert>

          <!-- Global Payment Settings -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="font-weight-bold">
              Payment Details (Applied to all selected invoices)
            </v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <DatePickerWithMenu
                    label="Payment Date"
                    :default="dateInvoicesPaid"
                    @date-selected="getSelectedPaymentDate"
                  ></DatePickerWithMenu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="paymentMethod"
                    :items="paymentMethods"
                    label="Payment Method"
                    outlined
                    dense
                    prepend-inner-icon="mdi-credit-card"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Individual Invoice Payments -->
          <div
            v-for="(invoice, ind) in selectedInvoicesForPayment"
            :key="invoice.id"
          >
            <v-card outlined class="invoice-payment-card mb-3">
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" sm="1" class="text-center">
                    <v-avatar :color="getThemeColor" size="32">
                      <span class="white--text">{{ ind + 1 }}</span>
                    </v-avatar>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <div class="invoice-info" v-if="invoice.client">
                      <div class="font-weight-bold">
                        {{ invoice.invoice_number }}
                      </div>
                      <div class="text-caption" >{{ invoice.client.name }}</div>
                      <div class="text-caption grey--text">
                        {{ invoice.client.institution }}
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="3">
                    <div class="payment-summary">
                      <div class="text-caption">Total Amount</div>
                      <div class="font-weight-bold">
                        {{ getCurrency
                        }}{{ formatNumber(invoice.calculation.total) }}
                      </div>
                      <div class="text-caption">
                        Paid: {{ getCurrency
                        }}{{ formatNumber(invoice.totalPayment) }}
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      :label="'Payment Amount (' + getCurrency + ')'"
                      outlined
                      dense
                      type="number"
                      :value="getPaymentAmount(invoice, ind)"
                      @input="updatePaymentAmount(ind, $event)"
                      prepend-inner-icon="mdi-cash-multiple"
                      :hint="
                        'Balance: ' +
                        getCurrency +
                        formatNumber(
                          invoice.calculation.total - invoice.totalPayment
                        )
                      "
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Quick Payment Buttons -->
                <v-row class="mt-2">
                  <v-col>
                    <div class="quick-payment-btns">
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click="
                          setPaymentAmount(
                            ind,
                            invoice.calculation.total - invoice.totalPayment
                          )
                        "
                      >
                        Full Balance
                      </v-btn>
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click="
                          setPaymentAmount(
                            ind,
                            (invoice.calculation.total - invoice.totalPayment) /
                              2
                          )
                        "
                      >
                        50%
                      </v-btn>
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click="setPaymentAmount(ind, 0)"
                      >
                        Clear
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <!-- Payment Summary -->
          <v-card outlined color="grey lighten-4">
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="summary-item">
                    <span class="summary-label">Total Payment:</span>
                    <span class="summary-value">
                      {{ getCurrency
                      }}{{ formatNumber(calculateTotalPayment()) }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="summary-item">
                    <span class="summary-label">Remaining Balance:</span>
                    <span class="summary-value">
                      {{ getCurrency
                      }}{{ formatNumber(calculateRemainingBalance()) }}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn outlined @click="closeDialog"> Cancel </v-btn>
          <v-btn
            :color="getThemeColor"
            dark
            large
            @click="markInvoicePaidNow()"
            :loading="markingPaid"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Confirm Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <GlobalConfirmDeleteDialog
      :item="confirmDeleteTitle"
      @delete-confirmed="takeActionAfterInvoiceDeleteChoice"
    ></GlobalConfirmDeleteDialog>

    <!-- Loading Overlay -->
    <v-overlay :value="sendingReminders" z-index="999">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
      <div class="mt-4 white--text">Sending reminders...</div>
    </v-overlay>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import pdfMakeParts from "@/plugins/pdfMakeParts";
import DatePickerWithMenu from "@/components/myLibrary/DatePickerWithMenu.vue";
import GlobalConfirmDeleteDialog from "@/components/myLibrary/GlobalConfirmDeleteDialog.vue";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default {
  name: "ModernInvoices",
  props: ["invoices"],

  components: {
    DatePickerWithMenu,
    GlobalConfirmDeleteDialog,
  },

  data() {
    return {
      searchInvoices: null,
      selectedInvoicesForPayment: [],
      selectedInvoice: null,
      selectedInvoices: [],
      markPaidDialog: false,
      dateInvoicesPaid: new Date().toISOString().substr(0, 10),
      paymentMethod: "Cash",
      paymentMethods: [
        "Cash",
        "Checks",
        "Credit Cards",
        "Debit Cards",
        "EFT",
        "Mobile Payments",
      ],
      invoiceHeaders: [
        { text: "Actions", value: "selection", sortable: false },
        { text: "Invoice #", value: "invoice_number" },
        { text: "Date", value: "invoice_date" },
        { text: "Client", value: "client.name" },
        { text: "Total", value: "calculation.total" },
        { text: "Payment", value: "paid" },
        { text: "Status", value: "status" },
        { text: "Due Date", value: "due_date" },
      ],
      confirmDeleteTitle: null,
      table: null,
      markingPaid: false,
      sendingReminders: false,
    };
  },

  computed: {
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getOutlineColor() {
      return this.$store.getters.getOutlineColor;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
    filteredInvoices() {
      if (!this.searchInvoices) {
        return this.invoices;
      }
      const search = this.searchInvoices.toLowerCase();
      return this.invoices.filter((invoice) => {
        return (
          (invoice.invoice_number && invoice.invoice_number.toLowerCase().includes(search)) || 
          (invoice.client && invoice.client.name && invoice.client.name.toLowerCase().includes(search))
           ||
          (invoice.case && invoice.case.title && invoice.case.title.toLowerCase().includes(search))
        );
      });
    },
  },

  mounted() {
    this.determineNewPayment();
    this.sendDueInvoiceReminders();
  },

  methods: {
    formatNumber(value) {
      if (!value) return "0.00";
      return Number(value).toFixed(2);
    },

    determineNewPayment() {
      if (this.invoices.length > 0) {
        this.invoices.forEach((invoice) => {
          if (invoice && invoice.calculation) {
            if (invoice && !invoice.newPayment) {
              invoice.newPayment = Number(
                invoice.calculation.total - invoice.totalPayment
              );
            }
          }
        });
      }
    },

    openMarkPaidDialog() {
      if (this.selectedInvoicesForPayment.length < 1) {
        this.$toast.warning("Please select at least one invoice",{duration:10000});
        return;
      }
      this.markPaidDialog = true;
    },

    getPaymentAmount(invoice, ind) {
      if (invoice.newPayment !== undefined && invoice.newPayment !== null) {
        return invoice.newPayment;
      }
      return Number(invoice.calculation.total - invoice.totalPayment);
    },

    updatePaymentAmount(ind, value) {
      this.selectedInvoicesForPayment[ind].newPayment = Number(value);
    },

    setPaymentAmount(ind, amount) {
      this.selectedInvoicesForPayment[ind].newPayment = Number(amount);
      this.$forceUpdate();
    },

    calculateTotalPayment() {
      return this.selectedInvoicesForPayment.reduce((sum, invoice) => {
        const payment =
          invoice.newPayment !== undefined && invoice.newPayment !== null
            ? invoice.newPayment
            : invoice.calculation.total - invoice.totalPayment;
        return sum + Number(payment);
      }, 0);
    },

    calculateRemainingBalance() {
      return this.selectedInvoicesForPayment.reduce((sum, invoice) => {
        const payment =
          invoice.newPayment !== undefined && invoice.newPayment !== null
            ? invoice.newPayment
            : invoice.calculation.total - invoice.totalPayment;
        const remaining =
          invoice.calculation.total - invoice.totalPayment - Number(payment);
        return sum + remaining;
      }, 0);
    },

    getSelectedPaymentDate(dateInvoicesPaid) {
      this.dateInvoicesPaid = dateInvoicesPaid;
    },

    editSelectedInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.$emit("edit-invoice", invoice);
    },

    markInvoicePaidNow() {
      this.markingPaid = true;
      this.$store.commit("setLoader", true);

      axios
        .postDataWithToken(
          "/auth/mark-invoices-paid",
          {
            selectedInvoicesForPayment: this.selectedInvoicesForPayment,
            dateInvoicesPaid: this.dateInvoicesPaid,
            paymentMethod: this.paymentMethod,
          },
          this.getAccessToken
        )
        .then((response) => {
          this.$emit("refresh-needed");
          this.closeDialog();
          this.$store.commit("setSnackbar", {
            show: true,
            message: "Invoices marked as paid successfully",
            color: "success",
          });
        })
        .catch((error) => {
          this.$store.commit("setSnackbar", {
            show: true,
            message: "Failed to mark invoices as paid",
            color: "error",
          });
        })
        .finally(() => {
          this.markingPaid = false;
          this.$store.commit("setLoader", false);
        });
    },

    determineColor(invoice) {
      if (!invoice || !invoice.calculation) return "grey";

      if (invoice.calculation.total === invoice.totalPayment) {
        return "#006064";
      } else if (this.isOverdue(invoice.due_date)) {
        return "error";
      } else {
        return "#01579B";
      }
    },

    isOverdue(dueDate) {
      const today = new Date().toISOString().substr(0, 10);
      return dueDate < today;
    },

    getInvoiceStatus(invoice) {
      if (invoice.totalPayment >= invoice.calculation.total) {
        return "Paid";
      } else if (this.isOverdue(invoice.due_date)) {
        return "Overdue";
      } else {
        return "Pending";
      }
    },

    getStatusText(invoice) {
      if (invoice.totalPayment >= invoice.calculation.total) {
        return "Paid";
      } else {
        const due = invoice.calculation.total - invoice.totalPayment;
        return `Due ${this.getCurrency}${this.formatNumber(due)}`;
      }
    },

    async printPaymentReceipt(invoice) {
      if (!invoice || !invoice.calculation) return;

      const balance = invoice.calculation.total - invoice.totalPayment;
      const isPaidInFull = balance <= 0;

      const docDefinition = {
        pageSize: "A4",
        pageMargins: [40, 60, 40, 60],
        content: [
          // Header
          {
            text: "PAYMENT RECEIPT",
            style: "header",
            alignment: "center",
            margin: [0, 0, 0, 10],
          },

          // Receipt Number and Date
          {
            columns: [
              {
                width: "*",
                text: [
                  { text: "Receipt #: ", bold: true },
                  { text: `RCP-${invoice.invoice_number}` },
                ],
              },
              {
                width: "*",
                text: [
                  { text: "Date: ", bold: true },
                  { text: new Date().toLocaleDateString() },
                ],
                alignment: "right",
              },
            ],
            margin: [0, 0, 0, 10],
          },

          // Divider
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 },
            ],
            margin: [0, 0, 0, 10],
          },

          // Client Information
          {
            text: "CLIENT INFORMATION",
            style: "subheader",
            margin: [0, 0, 0, 10],
          },
          {
            columns: [
              {
                width: "50%",
                stack: [
                  {
                    text: [{ text: "Name: ", bold: true }, invoice.client.name],
                  },
                  invoice.client.institution
                    ? {
                        text: [
                          { text: "Institution: ", bold: true },
                          invoice.client.institution,
                        ],
                        margin: [0, 5, 0, 0],
                      }
                    : {},
                  invoice.client.email
                    ? {
                        text: [
                          { text: "Email: ", bold: true },
                          invoice.client.email,
                        ],
                        margin: [0, 5, 0, 0],
                      }
                    : {},
                ],
              },
              {
                width: "50%",
                stack: [
                  invoice.client.phone
                    ? {
                        text: [
                          { text: "Phone: ", bold: true },
                          invoice.client.phone,
                        ],
                      }
                    : {},
                  invoice.case
                    ? {
                        text: [
                          { text: "Case: ", bold: true },
                          invoice.case.title,
                        ],
                        margin: [0, 5, 0, 0],
                      }
                    : {},
                ],
              },
            ],
            margin: [0, 0, 0, 10],
          },

          // Divider
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 },
            ],
            margin: [0, 0, 0, 10],
          },

          // Invoice Details
          {
            text: "INVOICE DETAILS",
            style: "subheader",
            margin: [0, 0, 0, 10],
          },
          {
            table: {
              widths: ["*", "*"],
              body: [
                [
                  { text: "Invoice Number", bold: true, fillColor: "#f5f5f5" },
                  invoice.invoice_number,
                ],
                [
                  { text: "Invoice Date", bold: true, fillColor: "#f5f5f5" },
                  invoice.invoice_date,
                ],
                [
                  { text: "Due Date", bold: true, fillColor: "#f5f5f5" },
                  invoice.due_date,
                ],
              ],
            },
            margin: [0, 0, 0, 10],
          },

          // Payment Information
          {
            text: "PAYMENT INFORMATION",
            style: "subheader",
            margin: [0, 0, 0, 10],
          },
          {
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  { text: "Invoice Total", bold: true },
                  {
                    text: `${this.getCurrency}${this.formatNumber(
                      invoice.calculation.total
                    )}`,
                    alignment: "right",
                  },
                ],
                [
                  { text: "Total Paid", bold: true, fillColor: "#e8f5e9" },
                  {
                    text: `${this.getCurrency}${this.formatNumber(
                      invoice.totalPayment
                    )}`,
                    alignment: "right",
                    fillColor: "#e8f5e9",
                  },
                ],
                [
                  {
                    text: "Balance Remaining",
                    bold: true,
                    fillColor: balance > 0 ? "#fff3e0" : "#e8f5e9",
                  },
                  {
                    text: `${this.getCurrency}${this.formatNumber(
                      Math.max(0, balance)
                    )}`,
                    alignment: "right",
                    fillColor: balance > 0 ? "#fff3e0" : "#e8f5e9",
                    bold: true,
                  },
                ],
              ],
            },
            margin: [0, 0, 0, 10],
          },

          // Payment Status
          {
            text: isPaidInFull
              ? "STATUS: PAID IN FULL"
              : "STATUS: PARTIAL PAYMENT",
            style: "status",
            alignment: "center",
            color: isPaidInFull ? "#2e7d32" : "#f57c00",
            margin: [0, 20, 0, 20],
          },

          // Footer Note
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 },
            ],
            margin: [0, 0, 0, 10],
          },
          {
            text: "Thank you for your payment!",
            style: "footer",
            alignment: "center",
          },
          {
            text: "This is a computer-generated receipt and does not require a signature.",
            style: "smallFooter",
            alignment: "center",
            margin: [0, 10, 0, 0],
          },
        ],
        styles: {
          header: {
            fontSize: 24,
            bold: true,
            color: "#1976d2",
          },
          subheader: {
            fontSize: 14,
            bold: true,
            color: "#424242",
          },
          status: {
            fontSize: 16,
            bold: true,
          },
          footer: {
            fontSize: 12,
            color: "#757575",
          },
          smallFooter: {
            fontSize: 9,
            color: "#9e9e9e",
            italics: true,
          },
        },
      };

      docDefinition.content.unshift(
        await pdfMakeParts.companyHeader("Receipt")
      );

      pdfMake
        .createPdf(docDefinition)
        .download(`payment-receipt-${invoice.invoice_number}.pdf`);
    },

    deleteSelectedInvoices() {
      if (this.selectedInvoicesForPayment.length < 1) {
        this.$toast.error("Please select at least one invoice",{duration:10000});
        return;
      }
      this.$store.commit("setGlobalConfirmDelete", true);
      this.confirmDeleteTitle = "Selected Invoices";
      this.table = "invoices";
    },

    takeActionAfterInvoiceDeleteChoice(choice) {
      if (choice) {
        if (this.table === "invoices") {
          this.selectedInvoices = [];
          this.selectedInvoicesForPayment.forEach((invoice) => {
            this.selectedInvoices.push(invoice.id);
          });
          this.bulkDeleteItems(this.selectedInvoices);
        }
      } else {
        this.$store.commit("setGlobalConfirmDelete", false);
      }
    },

    bulkDeleteItems(rowIds) {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/bulk-delete",
          { table: this.table, rowIds },
          this.getAccessToken
        )
        .then((response) => {
          this.$emit("refresh-needed");
          this.$store.commit("setSnackbar", {
            show: true,
            message: "Invoices deleted successfully",
            color: "success",
          });
        })
        .catch((error) => {
          this.$store.commit("setSnackbar", {
            show: true,
            message: "Failed to delete invoices",
            color: "error",
          });
        })
        .finally(() => {
          this.$store.commit("setLoader", false);
          this.closeDialog();
        });
    },

    sendDueInvoiceReminders() {
      const today = new Date().toISOString().substr(0, 10);
      const dueInvoices = this.invoices.filter((invoice) => {
        return (
          invoice.due_date === today &&
          invoice.totalPayment < invoice.calculation.total
        );
      });

      if (dueInvoices.length > 0) {
        this.sendingReminders = true;
        axios
          .postDataWithToken(
            "/auth/send-invoice-due-reminder",
            { invoices: dueInvoices },
            this.getAccessToken
          )
          .then((response) => {
            console.log("Reminders sent successfully");
          })
          .catch((error) => {
            console.error("Failed to send reminders:", error);
          })
          .finally(() => {
            this.sendingReminders = false;
          });
      }
    },

    closeDialog() {
      this.selectedInvoicesForPayment = [];
      this.markPaidDialog = false;
      this.confirmDeleteTitle = null;
      this.markingPaid = false;
    },
  },
};
</script>

<style scoped>
.invoices-container {
  padding: 16px;
  background: #f5f5f5;
  min-height: 100vh;
}

.header-card {
  background: white;
  border-radius: 12px;
  padding: 6px;
  margin-bottom: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.search-field {
  max-width: 400px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
}

/* Mobile View Styles */
.mobile-view .invoice-card {
  border-radius: 12px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.mobile-view .invoice-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.invoice-number {
  font-weight: 700;
  font-size: 16px;
}

.status-chip {
  font-weight: 600;
}

.invoice-detail {
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 8px;
}

.detail-label {
  font-weight: 600;
  color: #666;
  min-width: 80px;
}

.detail-value {
  color: #212121;
}

.case-title {
  color: #757575;
  font-style: italic;
}

.text-error {
  color: #d32f2f;
  font-weight: 600;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #757575;
}

/* Desktop View Styles */
.desktop-view {
  border-radius: 12px;
  overflow: hidden;
}

.modern-table {
  background: white;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.client-info .client-name {
  font-weight: 600;
  color: #212121;
}

.client-info .client-case {
  font-size: 12px;
  color: #757575;
  font-style: italic;
}

.amount-text {
  font-weight: 600;
  color: #1976d2;
}

/* Dialog Styles */
.mark-paid-card {
  border-radius: 12px;
}

.dialog-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 700;
}

.invoice-payment-card {
  border-radius: 8px;
  transition: all 0.2s;
}

.invoice-payment-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.invoice-info {
  line-height: 1.6;
}

.payment-summary {
  line-height: 1.8;
}

.quick-payment-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.summary-label {
  font-weight: 600;
  color: #424242;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #1976d2;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    align-items: stretch;
  }

  .search-field {
    max-width: 100%;
  }

  .action-buttons {
    justify-content: stretch;
  }

  .action-btn {
    flex: 1;
  }

  .page-title {
    font-size: 24px;
  }
}

@media (max-width: 600px) {
  .invoices-container {
    padding: 8px;
  }

  .header-card {
    padding: 16px;
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 20px;
  }

  .invoice-detail {
    flex-direction: column;
    align-items: flex-start;
    padding: 6px 0;
  }

  .detail-label {
    min-width: auto;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.invoice-card,
.invoice-payment-card {
  animation: fadeIn 0.3s ease-out;
}
</style>
